import clsx from "clsx";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "react-query";
import { twMerge } from "tailwind-merge";
import BlackWarningToast from "../../../../components/toast/BlackWarningToast";
import { useBookingState } from "../../../../hooks/useBookingState";
import { useKeyTranslatation } from "../../../../hooks/useKeyTranslatation";
import ProductService from "../../../../services/productService";
import {
  parseFreeForAges,
  ProdAttrUtils,
} from "../../../../utils/productAttributeUtils";
import SelectProductModal from "./SelectProductModal";
import { TtCategoryLs } from "./TtCategoryLs";
import Icon from "../../../../components/image/Icon";
import { icPremiumPlusTag } from "../../../../components/image/image-src";
import { useSearchParamsOfBooking } from "../../../../hooks/searchParams/useSearchParamsOfBooking";
import { toAgeString } from "../../../../utils/common";
import { useTranslationEx } from "../../../../hooks/useTranslationEx";
const productTypes: TProductType[] = ["TICKET", "TOUR", "SIM"]; // "MUSICAL"
const usePassProductListPicker = ({ d }: ProdAttrPickerModeProps) => {
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] =
    useState<ProductOfPassDTO | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState<TProductType>(
    productTypes[0],
  );
  const [allProducts, setAllProducts] = useState<ProductOfPassDTO[]>([]);
  const pass = d!;
  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useInfiniteQuery<PageResult<ProductOfPassDTO> | undefined>(
      ["products", "pass", pass.id, selectedProductType, selectedCategory],
      async ({ pageParam }) => {
        const response = await ProductService.getTicketByPass({
          id: pass.id!,
          pageIndex: pageParam,
          pageSize: 99999,
          // productType: selectedProductType,
          categoryId: selectedCategory,
        });
        return response;
      },
      {
        enabled: !!pass.id,
        getNextPageParam: (lastPage, pages) =>
          lastPage?.last ? undefined : pages.length,
        onSuccess: (newData) => {
          if (selectedCategory === null && newData) {
            const all = newData.pages.flatMap((p) => p?.data ?? []);
            setAllProducts(all);
          }
        },
      },
    );
  const loading = isLoading || isFetching;
  const products = data?.pages.flatMap((p) => p?.data);
  const handleCategoryClick = (cateId: number | null) => {
    setSelectedCategory(cateId);
  };
  const handleProductClick = useCallback((product: ProductOfPassDTO) => {
    setSelectedProduct(product);
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedProduct(null);
  }, []);
  return {
    selectedCategory,
    setSelectedCategory,
    selectedProductType,
    setSelectedProductType,
    products,
    loading,
    fetchNextPage,
    hasNextPage,
    handleCategoryClick,
    handleProductClick,
    modalOpen,
    setModalOpen,
    selectedProduct,
    setSelectedProduct,
    closeModal,
    allProducts,
  };
};

export type TPassProductListPickerProps = ReturnType<
  typeof usePassProductListPicker
>;

export const PassProductListPicker = (props: ProdAttrPickerModeProps) => {
  const { t } = useTranslation();
  const { d } = props;
  const jj = usePassProductListPicker(props);
  if (!d) return null;

  return (
    <div className="flex flex-col">
      <p className="mb-2 mt-5 text-sm font-normal text-[#5E6D77] sm:text-base sm:leading-[1.5rem]">
        {t("Select Product")}
      </p>
      {/* <TtProductTypeTabs {...jj} /> */}
      <TtCategoryLs {...jj} />
      <TtProducts {...jj} />
      {jj.modalOpen && jj.selectedProduct && (
        <SelectProductModal
          id={Number(jj.selectedProduct.id)}
          slug={jj.selectedProduct.slug}
          isOpen={jj.modalOpen}
          closeModal={jj.closeModal}
        />
      )}
    </div>
  );
};

const TtProducts: FC<TPassProductListPickerProps> = (jj) => {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-5 md:grid-cols-3 lg:grid-cols-4">
      {jj.products?.map((product) => (
        <TtCard key={product?.id!} jj={jj} product={product!} />
      ))}
    </div>
  );
};

const TtHighlightText = (props: {
  vari: "RED" | "BLUE" | "GREEN";
  text: string;
}) => (
  <div
    className={clsx(
      "absolute bottom-0 flex min-h-[26px] w-full items-center justify-center gap-[10px] px-[10px] py-[4px]",
      {
        "bg-[#F8D7D7]": props.vari === "RED",
        "bg-[#DBE7FF]": props.vari === "BLUE",
        "bg-[#C6EFCE]": props.vari === "GREEN",
      },
    )}
  >
    <span
      className={clsx("text-xs font-semibold leading-[18px]", {
        "text-[#DA3838]": props.vari === "RED",
        "text-[#007AFF]": props.vari === "BLUE",
        "text-[#006100]": props.vari === "GREEN",
      })}
    >
      {props.text}
    </span>
  </div>
);

export const checkCanEdit = ({
  bookingState,
  isSelected,
  isUpgrade,
  isEdit,
  productId,
}: {
  bookingState: BookingState;
  isSelected: boolean;
  isUpgrade?: boolean;
  isEdit?: boolean;
  productId?: number;
}) => {
  return !!(
    (isUpgrade &&
      (!isSelected ||
        // *BOOKMARK* NEWLY PICKED PRODUCT
        bookingState.childrenLs?.find((x) => x?.product?.id === productId)
          ?.canEdit === undefined)) ||
    (isEdit &&
      (!isSelected ||
        bookingState.childrenLs?.find((x) => x?.product?.id === productId)
          ?.canEdit !== false)) ||
    (!isEdit && !isUpgrade)
  );
};
const TtCard: FC<{
  jj: TPassProductListPickerProps;
  product: ProductOfPassDTO;
}> = ({ product, jj }) => {
  const { isUpgrade, isEdit } = useSearchParamsOfBooking();
  const { t } = useTranslation();
  const { t2 } = useTranslationEx();
  const bookingState = useBookingState({
    mode: "FORM",
  });
  const selectedProducts = bookingState.childrenLs?.map((x) => x.product) ?? [];
  const isSelected = selectedProducts.some(
    (selectedProduct) => product.id && selectedProduct?.id === product.id,
  );

  const canEdit = checkCanEdit({
    bookingState,
    isSelected,
    isUpgrade,
    isEdit,
    productId: product.id,
  });

  // console.log("bookingState.childrenLs", bookingState.childrenLs);
  const [showMaxWarning, setShowMaxWarning] = useState(false);
  const max = ProdAttrUtils.PASS.findMaxProduct(bookingState);
  const ageLs = ProdAttrUtils.parseAllChildAges(bookingState);
  const minAge = Math.min(...ageLs);
  const isRequireAge = !!product.requiredAge && product.requiredAge > minAge;
  // findPremiumPlusDpPrice({
  //   product,
  // });

  const freeForAgeText = useMemo(() => {
    return !!product?.freeForAges &&
      ageLs.find((x) => parseFreeForAges(product?.freeForAges).includes(+x)) !==
        undefined
      ? (product.freeForAges ?? "").replaceAll(",", ", ").replaceAll("_", "-")
      : false;
  }, [product?.freeForAges, ageLs]);

  const isDisabledDueToAge = !!(isRequireAge || freeForAgeText);
  // const isHidden =  isEdit && !isSelected ;
  const isHidden = bookingState?.canceledProductIds?.includes(product.id ?? 0);
  return (
    <div
      key={product.id}
      onClick={() => {
        if (!canEdit) {
          return;
        }
        if (isDisabledDueToAge && !isSelected) {
          console.warn("Not allowed to select this product");
          return;
        }
        if (selectedProducts.length >= max && !isSelected) {
          setShowMaxWarning(true);
          return;
        }
        jj.handleProductClick(product!);
      }}
      className={clsx(
        "flex h-full flex-col overflow-hidden rounded-[0.75rem] border bg-white",
        isSelected ? "border-primary-light" : "border-[#D7DCE3]",
        !canEdit
          ? isEdit || isUpgrade
            ? "cursor-not-allowed !border-[#232323]"
            : "cursor-not-allowed opacity-50"
          : "cursor-pointer",
        isHidden ? "hidden" : "",
      )}
    >
      <div className="relative">
        {!!product.premiumPlusPrice && (
          <span className="absolute right-[0.5rem] top-[0.5rem]">
            <Icon src={icPremiumPlusTag} />
          </span>
        )}
        <img
          className={clsx(
            "h-[7rem] w-full object-cover",
            isDisabledDueToAge && "pointer-events-none opacity-30",
          )}
          src={product.thumbnail}
          alt={product.name}
        />
        <AgeHighlight
          isRequireAge={isRequireAge}
          product={product}
          freeForAgeText={freeForAgeText}
        />
      </div>
      <div className="flex flex-col px-2 py-4">
        {(product.productType === "TICKET" ||
          product.productType === "TOUR") && (
          <span className="w-fit rounded-[0.3125rem] bg-[#F7F8FA] px-[0.5rem] py-1 text-[0.75rem] leading-[1.125rem] text-[#5E6D77]">
            {t2(product.categoryName)}
          </span>
        )}

        <p className="mt-2 text-xs font-semibold text-[#232323]">
          {t2(product.name)}
        </p>
      </div>
      {showMaxWarning && (
        <BlackWarningToast
          onClose={() => {
            setShowMaxWarning(false);
          }}
          message={t("You have reached the maximum allowed selection")}
        />
      )}
    </div>
  );
};

const AgeHighlight = ({
  product,
  isRequireAge,
  freeForAgeText,
}: {
  product: ProductOfPassDTO;
  isRequireAge: boolean;
  freeForAgeText: string | false;
}) => {
  const { t } = useTranslation();

  return (
    <>
      {isRequireAge && (
        <TtHighlightText
          vari="RED"
          text={t("Entry allowed from age ", {
            age: toAgeString(product.requiredAge + ""),
          })}
        />
      )}
      {freeForAgeText && (
        <TtHighlightText
          vari="GREEN"
          text={t("FREE for children ages ", { age: freeForAgeText })}
        />
      )}
    </>
  );
};

const TtProductTypeTabs: FC<TPassProductListPickerProps> = (jj) => {
  const t = useKeyTranslatation();
  return (
    <div className="flex h-12 gap-10">
      {productTypes.map((type) => (
        <TtProductTypeTab
          onClick={() => {
            jj.setSelectedProductType(type);
            // jj.setSelectedCategory(null);
          }}
          className={
            jj.selectedProductType === type
              ? "border-[#232323] text-[#232323]"
              : ""
          }
          key={type}
        >
          {t(type)}
        </TtProductTypeTab>
      ))}
    </div>
  );
};

const TtProductTypeTab: FcBtn = ({ onClick, children, className }) => {
  return (
    <button
      onClick={onClick}
      type="button"
      className={twMerge(
        "border-b border-white font-semibold leading-[22px] text-[#5E6D77]",
        className,
      )}
    >
      {children}
    </button>
  );
};
